/**
 * Orchestrator for the 3-screen passwordless email-OTP sign-in flow. Owns
 * cross-screen state (screen, email) and composes the three independent
 * step elements as tags. Data flows down via properties; intent flows up
 * via CustomEvents.
 *
 * Zero I/O: every screen transition dispatches a zero-I/O action through
 * sign.state.ts's signAction$ channel and awaits its keyed signResult$
 * before updating local state — session.sync.ts (web-side) is the sole
 * subscriber, performing the Firebase Auth + Firestore work. Each dispatch
 * subscribes to the keyed result BEFORE emitting the action so a
 * synchronous reconcile can never resolve before the view is listening.
 *
 * Critical sequencing — never navigate here: dispatch verifyOtp →
 * session.sync signs in with the minted custom token → the identity leg's
 * onIdTokenChanged fires → session.state's needsConsent reads true (no
 * users/{uid} doc yet) → this view shows 'consent' → user accepts →
 * dispatch createAccount writes users/{uid} → the identity leg re-resolves
 * → needsConsent flips false → sessionReady flips true → the subscription
 * below dispatches SignedInEvent. Returning user: the doc already exists →
 * sessionReady flips true immediately; this view never shows 'consent'.
 *
 * Where a finished sign-in lands is the host's: each one answers
 * SignedInEvent with its own destination.
 *
 * The email step resolves the address FIRST (resolveEmail): a known address
 * goes straight on to a sendOtp and the code step; an unknown one never
 * asks for a code and lands on the no-account screen instead, so a stranger
 * is never told "code sent" about a mail that never goes out. That screen is
 * the same shell either way: a host that takes access requests sets
 * `noAccount` and renders its own form there, slotted as
 * `slot="no-account"` — the email step then also offers "No account? Request
 * access" for a person who already knows — while any other host sees the
 * prompt itself: the address in full, so a typo can be caught, and a lede.
 * "Edit address" returns to the email step with what the person typed kept.
 */
import {
	conditionalMediation$,
	webAuthnSupported$,
} from "@mhmo91/passkey/client";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { html, nothing, type PropertyValues } from "lit";
import { property, state } from "lit/decorators.js";
import { choose } from "lit/directives/choose.js";
import { createRef, ref } from "lit/directives/ref.js";
import {
	combineLatest,
	EmptyError,
	filter,
	firstValueFrom,
	map,
	merge,
	takeUntil,
} from "rxjs";
import "@mhmo91/schmancy/visually-hidden";
import { LocalizeMixin } from "../i18n/localize.ts";
import { brandAbsent } from "../workspace/workspace.state.ts";
import "../workspace/brand-logo.view.ts";
import "../storefront/revia-logo.view.ts";
import "./sign-email.view.ts";
import "./sign-code.view.ts";
import "./sign-consent.view.ts";
import "./sign-shell.view.ts";
import { session, sessionReady, sessionRetry$ } from "./session.state.ts";
import {
	type InviteContext,
	signAction$,
	signFlowState,
	signResult$,
} from "./sign.state.ts";
import type {
	SignCodeBackEvent,
	SignCodeResendEvent,
	SignCodeStep,
	SignCodeVerifyEvent,
} from "./sign-code.view.ts";
import type {
	SignConsentAcceptEvent,
	SignConsentBackEvent,
} from "./sign-consent.view.ts";
import type {
	SignEmailNoAccountEvent,
	SignEmailSubmitEvent,
} from "./sign-email.view.ts";

type Screen = "email" | "code" | "consent" | "noAccount";

/** Sign-in is complete; where the person goes next is the host's to answer. */
export class SignedInEvent extends Event {
	static readonly eventName = "signed-in" as const;
	constructor() {
		super(SignedInEvent.eventName, { bubbles: true, composed: true });
	}
}

@schmancyElement("sign-in")
export default class SignInView extends LocalizeMixin(SchmancyElement) {
	/** Set by Revia's backoffice, which reuses sign-in without a workspace card. */
	@property({ attribute: "brand-name" }) brandName: string | null = null;
	/** The host renders an access request form in `slot="no-account"`. */
	@property({ type: Boolean, attribute: "no-account" }) noAccount = false;
	@property({ type: Boolean, attribute: "hide-brand" }) hideBrand = false;

	@state() private screen: Screen = "email";
	@state() private email = "";
	/** The address the email step reopens with after the no-account screen;
	 *  empty on every other way back, so the step opens as it always has. */
	@state() private draftEmail = "";
	@state() private loading = false;
	@state() private passkeySupported = false;
	@state() private passkeyLoading = false;
	@state() private passkeyError = "";
	private passkeyAbort?: AbortController;
	@state() private pendingAction: "verify" | "resend" | null = null;
	@state() private error = "";
	@state() private handoff: "checking" | "opening" | null = null;
	private completedUid: string | null = null;
	private actionEpoch = 0;
	/** sendOtp's own invite-context projection — present only while this
	 *  email holds a live pending invite; rendered by sign-code/sign-consent. */
	@state() private invite: InviteContext | null = null;

	/** Firebase-Auth identity resolved by verifyOtp — carried forward to the
	 *  createAccount dispatch on consent accept. */
	private pendingUid = "";
	private pendingDisplayName = "";

	private codeStepRef = createRef<SignCodeStep>();

	override connectedCallback() {
		super.connectedCallback();

		// Restore position from session state (survives an accidental nav away
		// and back).
		const persisted = signFlowState.value;
		if (persisted.screen !== "email" || persisted.email) {
			this.screen = persisted.screen;
			this.email = persisted.email;
			this.invite = persisted.invite;
			this.pendingUid = persisted.pendingUid ?? "";
			this.pendingDisplayName = persisted.pendingDisplayName ?? "";
		}

		merge(
			webAuthnSupported$.pipe(
				map((supported) => ({ kind: "support", supported }) as const),
			),
			combineLatest([conditionalMediation$, session.$]).pipe(
				filter(([supported, live]) => supported && live.status === "anonymous"),
				map(() => ({ kind: "conditional" }) as const),
			),
			signResult$.pipe(
				filter((result) => result.kind === "signOut" && result.ok),
				map(() => ({ kind: "signedOut" }) as const),
			),
			session.$.pipe(map((live) => ({ kind: "session", live }) as const)),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe((event) => {
				if (event.kind === "support") {
					this.passkeySupported = event.supported;
					return;
				}
				if (event.kind === "conditional") {
					if (this.screen === "email" && this.handoff === null)
						this.onPasskey(true);
					return;
				}
				if (event.kind === "signedOut") {
					this.actionEpoch++;
					this.passkeyAbort?.abort();
					this.handoff = null;
					this.loading = false;
					this.passkeyLoading = false;
					this.pendingAction = null;
					this.screen = "email";
					this.email = "";
					this.draftEmail = "";
					this.invite = null;
					this.pendingUid = "";
					this.pendingDisplayName = "";
					this.completedUid = null;
					signFlowState.replace({ screen: "email", email: "", invite: null });
					return;
				}
				if (sessionReady.get() && event.live.uid !== null) {
					this.handoff = "opening";
					signFlowState.replace({ screen: "email", email: "", invite: null });
					return;
				}
				if (event.live.status === "anonymous") {
					this.completedUid = null;
					if (this.handoff !== null) {
						this.handoff = null;
						this.loading = false;
						this.pendingAction = null;
						this.screen = "email";
						this.draftEmail = this.email;
						this.error = this.t("auth.signIn.sessionExpired");
					}
					return;
				}
				if (event.live.status === "error") {
					this.handoff = null;
					this.loading = false;
					this.pendingAction = null;
					return;
				}
				if (
					event.live.status !== "member" ||
					!event.live.needsConsent ||
					event.live.uid === null
				)
					return;
				if (this.screen === "consent" && this.pendingUid === event.live.uid)
					return;
				this.handoff = null;
				this.loading = false;
				this.screen = "consent";
				this.pendingUid = event.live.uid;
				this.email = event.live.email ?? this.email;
				signFlowState.set({
					screen: "consent",
					email: this.email,
					pendingUid: this.pendingUid,
				});
			});
	}

	override updated(changed: PropertyValues) {
		super.updated(changed);
		if (
			this.isConnected &&
			this.handoff === "opening" &&
			sessionReady.get() &&
			session.value.uid !== this.completedUid
		) {
			this.completedUid = session.value.uid;
			this.dispatchEvent(new SignedInEvent());
		}
	}

	/** Awaits the keyed result for one dispatched action — subscribes BEFORE
	 *  emitting so a synchronous reconcile cannot resolve before we listen. */
	private awaitResult(requestId: string) {
		return firstValueFrom(
			signResult$.pipe(
				filter((r) => r.requestId === requestId),
				takeUntil(
					merge(
						this.disconnecting,
						signResult$.pipe(
							filter(
								(r) =>
									r.kind === "signOut" && r.ok && r.requestId !== requestId,
							),
						),
					),
				),
			),
		);
	}

	override disconnectedCallback() {
		this.actionEpoch++;
		this.passkeyAbort?.abort();
		super.disconnectedCallback();
	}

	private onPasskey(conditional = false) {
		if (this.loading || this.passkeyLoading || this.handoff !== null) return;
		if (conditional && this.passkeyAbort && !this.passkeyAbort.signal.aborted)
			return;
		if (!conditional) this.passkeyAbort?.abort();
		const controller = new AbortController();
		this.passkeyAbort = controller;
		if (!conditional) {
			this.passkeyLoading = true;
			this.passkeyError = "";
		}
		const requestId = crypto.randomUUID();
		const epoch = this.actionEpoch;
		this.awaitResult(requestId)
			.then((result) => {
				if (epoch !== this.actionEpoch || !this.isConnected) return;
				if (!conditional) this.passkeyLoading = false;
				if (result.ok) this.handoff ??= "checking";
				if (!result.ok && result.reason !== "cancelled" && !conditional)
					this.passkeyError = this.t("auth.signIn.passkeyRefused");
			})
			.catch((error: unknown) => {
				if (
					error instanceof EmptyError ||
					epoch !== this.actionEpoch ||
					!this.isConnected
				)
					return;
				if (!conditional) this.passkeyLoading = false;
			});
		signAction$.next({
			kind: "passkeySignIn",
			requestId,
			...(conditional ? { mediation: "conditional" as const } : {}),
			signal: controller.signal,
		});
	}

	// ── Email screen handlers ────────────────────────────────────────────────

	/** Resolve first, then send: the code is asked for only once the server
	 *  says this address would be mailed one, so an unknown address lands on
	 *  the no-account screen with no code promised. Both answers are awaited
	 *  under one busy state; the email step shows nothing between them. */
	private onEmailSubmit(e: SignEmailSubmitEvent) {
		this.passkeyAbort?.abort();
		if (this.loading) return;
		this.draftEmail = "";
		this.email = e.detail.email;
		this.error = "";
		this.loading = true;
		const resolveId = crypto.randomUUID();
		const epoch = this.actionEpoch;
		this.awaitResult(resolveId)
			.then((resolved) => {
				if (epoch !== this.actionEpoch || !this.isConnected) return;
				if (!resolved.ok) {
					this.loading = false;
					this.error = this.t(
						resolved.reason === "rate-limited"
							? "auth.signIn.sendCodeRateLimited"
							: "auth.signIn.sendCodeFailed",
					);
					return undefined;
				}
				if (!resolved.known) {
					this.loading = false;
					this.openNoAccount(this.email);
					return undefined;
				}
				const sendId = crypto.randomUUID();
				const sent = this.awaitResult(sendId).then((result) => {
					if (epoch !== this.actionEpoch || !this.isConnected) return;
					this.loading = false;
					this.pendingAction = null;
					if (result.ok) {
						this.screen = "code";
						this.invite = result.invite ?? resolved.invite ?? null;
						signFlowState.set({
							screen: "code",
							email: this.email,
							invite: this.invite,
						});
					} else {
						this.error = this.t(
							result.reason === "rate-limited"
								? "auth.signIn.sendCodeRateLimited"
								: "auth.signIn.sendCodeFailed",
						);
					}
					return undefined;
				});
				signAction$.next({
					kind: "sendOtp",
					requestId: sendId,
					email: this.email,
				});
				return sent;
			})
			.catch((error: unknown) => {
				if (
					error instanceof EmptyError ||
					epoch !== this.actionEpoch ||
					!this.isConnected
				)
					return;
				this.loading = false;
				this.pendingAction = null;
			});
		signAction$.next({
			kind: "resolveEmail",
			requestId: resolveId,
			email: this.email,
		});
	}

	/** The person already knows there is no account and asks for access. */
	private onNoAccount(e: SignEmailNoAccountEvent) {
		if (this.loading) return;
		this.error = "";
		this.openNoAccount(e.detail.email);
	}

	/** The no-account screen, whether the server found no account for the
	 *  address or the person said so: the address is kept for the edit, and
	 *  a host's request form opens on it. */
	private openNoAccount(email: string) {
		this.draftEmail = email;
		this.screen = "noAccount";
		this.dispatchEvent(
			new CustomEvent("sign-in-no-account", {
				detail: { email },
				bubbles: true,
				composed: true,
			}),
		);
	}

	/** The address the person typed before choosing the no-account branch. */
	get noAccountEmail(): string {
		return this.draftEmail;
	}

	private onEditAddress() {
		this.screen = "email";
	}

	// ── Code screen handlers ─────────────────────────────────────────────────

	private onCodeVerify(e: SignCodeVerifyEvent) {
		if (this.loading) return;
		this.pendingAction = "verify";
		this.error = "";
		this.loading = true;
		const requestId = crypto.randomUUID();
		const epoch = this.actionEpoch;
		this.awaitResult(requestId)
			.then((result) => {
				if (epoch !== this.actionEpoch || !this.isConnected) return;
				if (!result.ok) {
					this.loading = false;
					this.pendingAction = null;
					// The code itself was right — only the invite it redeems has since
					// lapsed. Distinct copy: "wrong code" would blame the wrong thing.
					this.error =
						result.reason === "invite-expired"
							? this.t("auth.signIn.inviteExpired")
							: this.t("auth.signIn.codeIncorrect");
					this.codeStepRef.value?.clearCode();
					return undefined;
				}
				if (result.isNewUser) {
					this.loading = false;
					this.pendingAction = null;
					this.handoff = null;
					this.pendingUid = result.uid ?? "";
					this.pendingDisplayName = result.displayName ?? "";
					this.screen = "consent";
					signFlowState.set({
						screen: "consent",
						email: this.email,
						pendingUid: this.pendingUid,
						pendingDisplayName: this.pendingDisplayName,
						...(result.workspaceId === undefined
							? {}
							: { pendingWorkspaceId: result.workspaceId }),
					});
				} else {
					this.invite = null;
					this.handoff ??= "checking";
				}
				return undefined;
			})
			.catch((error: unknown) => {
				if (
					error instanceof EmptyError ||
					epoch !== this.actionEpoch ||
					!this.isConnected
				)
					return;
				this.loading = false;
				this.pendingAction = null;
			});
		signAction$.next({
			kind: "verifyOtp",
			requestId,
			email: this.email,
			code: e.detail.code,
		});
	}

	private onCodeResend(_e: SignCodeResendEvent) {
		if (this.loading) return;
		this.pendingAction = "resend";
		this.error = "";
		this.loading = true;
		const requestId = crypto.randomUUID();
		const epoch = this.actionEpoch;
		this.awaitResult(requestId)
			.then((result) => {
				if (epoch !== this.actionEpoch || !this.isConnected) return;
				this.loading = false;
				this.pendingAction = null;
				if (result.ok) {
					this.invite = result.invite ?? null;
					signFlowState.set({ invite: this.invite });
					this.codeStepRef.value?.startCooldown();
				} else {
					this.error = this.t(
						result.reason === "rate-limited"
							? "auth.signIn.sendCodeRateLimited"
							: "auth.signIn.resendFailed",
					);
				}
				return undefined;
			})
			.catch((error: unknown) => {
				if (
					error instanceof EmptyError ||
					epoch !== this.actionEpoch ||
					!this.isConnected
				)
					return;
				this.loading = false;
				this.pendingAction = null;
			});
		signAction$.next({ kind: "sendOtp", requestId, email: this.email });
	}

	private onCodeBack(_e: SignCodeBackEvent) {
		if (this.loading) return;
		this.screen = "email";
		this.error = "";
		this.invite = null;
		this.pendingUid = "";
		this.pendingDisplayName = "";
		// pendingUid/pendingDisplayName omitted — signFlowState.replace() fully
		// overwrites, so leaving them out clears the persisted identity
		// (exactOptionalPropertyTypes forbids `undefined` here).
		signFlowState.replace({
			screen: "email",
			email: "",
			invite: null,
		});
	}

	// ── Consent screen handlers ──────────────────────────────────────────────

	private onConsentAccept(e: SignConsentAcceptEvent) {
		if (this.loading) return;
		if (!this.pendingUid) {
			this.error = this.t("auth.signIn.sessionExpired");
			this.screen = "email";
			return;
		}
		this.error = "";
		this.loading = true;
		const requestId = crypto.randomUUID();
		const epoch = this.actionEpoch;
		this.awaitResult(requestId)
			.then((result) => {
				if (epoch !== this.actionEpoch || !this.isConnected) return;
				if (result.ok) {
					this.pendingUid = "";
					this.pendingDisplayName = "";
					this.handoff ??= "checking";
				} else {
					this.loading = false;
					this.pendingAction = null;
					this.error = this.t("auth.signIn.accountCreationFailed");
				}
				return undefined;
			})
			.catch((error: unknown) => {
				if (
					error instanceof EmptyError ||
					epoch !== this.actionEpoch ||
					!this.isConnected
				)
					return;
				this.loading = false;
				this.pendingAction = null;
			});
		signAction$.next({
			kind: "createAccount",
			requestId,
			uid: this.pendingUid,
			email: this.email,
			// verifyOtp's own resolved name (invite/Auth-account) is the source;
			// the email local-part is a last-resort UX default only when that
			// source is empty, so UserSchema.displayName.min(1) always holds.
			displayName: this.pendingDisplayName || this.email.split("@")[0] || "",
			// Read from the flow's own record, not this element's lifetime: the
			// verify that learned it may have answered a since-remounted element.
			workspaceId: signFlowState.value.pendingWorkspaceId ?? null,
			tosVersion: e.detail.tosVersion,
			privacyVersion: e.detail.privacyVersion,
			platform: e.detail.platform,
		});
	}

	// The verified address is already signed in pending consent, so a
	// different one starts from a signed-out session, or a reload would put
	// this address's consent back on screen.
	private onConsentBack(_e: SignConsentBackEvent) {
		if (this.loading) return;
		this.error = "";
		this.loading = true;
		const requestId = crypto.randomUUID();
		const epoch = this.actionEpoch;
		this.awaitResult(requestId)
			.then((result) => {
				if (epoch !== this.actionEpoch || !this.isConnected) return;
				this.loading = false;
				if (!result.ok) {
					this.error = this.t("auth.signIn.signOutFailed");
					return undefined;
				}
				return undefined;
			})
			.catch((error: unknown) => {
				if (
					error instanceof EmptyError ||
					epoch !== this.actionEpoch ||
					!this.isConnected
				)
					return;
				this.loading = false;
			});
		signAction$.next({ kind: "signOut", requestId });
	}

	// ── Render ─────────────────────────────────────────────────────────────

	override render() {
		// An active verification keeps its input mounted through the identity
		// swap. Once proven, the handoff owns the surface until its host routes.
		if (
			this.handoff !== null ||
			session.value.status === "binding" ||
			(session.value.status === "loading" &&
				!this.loading &&
				!this.passkeyLoading)
		) {
			return html`
				<sign-shell>
					<div class="flex items-center gap-3 px-4 py-6 sm:px-6" role="status" aria-live="polite">
						<schmancy-spinner size="sm" aria-hidden="true"></schmancy-spinner>
						<schmancy-typography type="body" token="md">${this.t(this.handoff === "opening" ? "auth.signIn.openingAccount" : this.handoff === "checking" ? "auth.signIn.signingIn" : "auth.signIn.checkingSession")}</schmancy-typography>
					</div>
				</sign-shell>
			`;
		}
		if (session.value.status === "error")
			return html`
			<sign-shell>
				<div class="flex flex-col items-start gap-3 px-4 py-6 sm:px-6" role="alert">
					<schmancy-typography type="title" token="md">${this.t("auth.connection.title")}</schmancy-typography>
					<schmancy-typography type="body" token="md">${this.t("auth.connection.body")}</schmancy-typography>
					<schmancy-button variant="filled" @click=${() => {
						this.handoff = "checking";
						sessionRetry$.next();
					}}>${this.t("auth.connection.retry")}</schmancy-button>
				</div>
			</sign-shell>`;

		return html`
			<sign-shell>
				${choose(this.screen, [
					[
						"email",
						() => html`
							<sign-email
								.loading=${this.loading}
								.passkeySupported=${this.passkeySupported}
								.passkeyLoading=${this.passkeyLoading}
								.passkeyError=${this.passkeyError}
								@sign-passkey=${() => this.onPasskey()}
								.error=${this.error}
								.email=${this.draftEmail}
								.noAccount=${this.noAccount}
								.hideBrand=${this.hideBrand}
								@sign-email-submit=${(e: SignEmailSubmitEvent) => this.onEmailSubmit(e)}
								@sign-email-no-account=${(e: SignEmailNoAccountEvent) => this.onNoAccount(e)}
							>
								${
									this.hideBrand
										? nothing
										: this.brandName !== null
											? html`<revia-logo animated unlinked
												slot="brand"
												aria-hidden="true"
												class="block h-48 w-48"
											></revia-logo>`
											: brandAbsent.get()
												? html`<brand-logo slot="brand" class="h-48 w-48"></brand-logo>
													<schmancy-typography
														slot="brand"
														role="heading"
														aria-level="1"
														type="headline"
														token="md"
														>${this.t("auth.signEmail.title")}</schmancy-typography
													>`
												: html`<brand-logo slot="brand" class="h-48 w-48"></brand-logo>`
								}
							</sign-email>
						`,
					],
					[
						"noAccount",
						() => html`
							<div class="flex flex-col gap-6 px-4 py-6 sm:px-6">
								${
									this.noAccount
										? html`<schmancy-typography
													role="heading"
													aria-level="1"
													type="headline"
													token="md"
													>${this.t("auth.signNoAccount.title")}</schmancy-typography
												>
												<slot name="no-account"></slot>`
										: html`<div class="flex flex-col gap-3">
													<div class="flex flex-col gap-1">
														<schmancy-typography
															role="heading"
															aria-level="1"
															type="headline"
															token="md"
															>${this.t("auth.signNoAccount.heading")}</schmancy-typography
														>
														<!-- The address in full, on its own line: this screen exists so
														     a mistyped address can be caught, and weight is what makes a
														     wrong character findable. -->
														<schmancy-typography type="title" token="md" class="break-words"
															>${this.draftEmail}</schmancy-typography
														>
													</div>
													<schmancy-typography type="body" token="md"
														>${this.t("auth.signNoAccount.lede")}</schmancy-typography
													>
												</div>`
								}
								<!-- Only the screen moves back; the address survives it, so fixing
								     a typo is an edit rather than a re-type. -->
								<schmancy-button
									variant=${this.noAccount ? "text" : "outlined"}
									width="auto"
									class="self-start"
									size="md"
									@click=${() => this.onEditAddress()}
								>
									${this.noAccount ? html`<schmancy-icon slot="prefix">arrow_back</schmancy-icon>` : ""}
									${this.t("auth.signNoAccount.editAddress")}
								</schmancy-button>
							</div>
						`,
					],
					[
						"code",
						() => html`
							<sign-code
								${ref(this.codeStepRef)}
								.email=${this.email}
								.pendingAction=${this.pendingAction}
								.loading=${this.loading}
								.passkeySupported=${this.passkeySupported}
								.passkeyLoading=${this.passkeyLoading}
								.passkeyError=${this.passkeyError}
								@sign-passkey=${() => this.onPasskey()}
								.error=${this.error}
								.invite=${this.invite}
								@sign-code-verify=${(e: SignCodeVerifyEvent) => this.onCodeVerify(e)}
								@sign-code-resend=${(e: SignCodeResendEvent) => this.onCodeResend(e)}
								@sign-code-back=${(e: SignCodeBackEvent) => this.onCodeBack(e)}
							></sign-code>
						`,
					],
					[
						"consent",
						() => html`
							<sign-consent
								.email=${this.email}
								.loading=${this.loading}
								.passkeySupported=${this.passkeySupported}
								.passkeyLoading=${this.passkeyLoading}
								.passkeyError=${this.passkeyError}
								@sign-passkey=${() => this.onPasskey()}
								.error=${this.error}
								.invite=${this.invite}
								@sign-consent-accept=${(e: SignConsentAcceptEvent) => this.onConsentAccept(e)}
								@sign-consent-back=${(e: SignConsentBackEvent) => this.onConsentBack(e)}
							></sign-consent>
						`,
					],
				])}
			</sign-shell>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"sign-in": SignInView;
	}
}
